import constants from 'constants/constants';
import { Controller, useForm } from 'react-hook-form';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';

import { useProjectTreeStore } from 'store/projectContract.store';

import { useMutationRenameSubProgram } from 'api/mutations/renameSubProgram.mutation';

import classes from 'components/common/ProgramLibraryDropdownMenu/components/RenameSubProgramModal/RenameSubProgramModal.module.scss';
import { CustomizedFormErrorMessage } from 'components/common/index';

import { Button } from 'components/DesignSystem/Button';
import { Modal } from 'components/DesignSystem/Modal';
import { TextInput } from 'components/DesignSystem/TextInput/TextInput';

interface Props {
    showModal: boolean;
    onHideModal: () => void;
}

interface IProjectName {
    contractName: string;
}

export const RenameSubProgramModal = ({ showModal, onHideModal }: Props) => {
    const { contract, update } = useProjectTreeStore();

    const { handleSnackBar } = useCustomSnackBar();
    const { mutate, isLoading } = useMutationRenameSubProgram();

    const {
        handleSubmit,
        control,
        formState: { errors },
        setValue,
        reset,
    } = useForm<IProjectName>({
        values: {
            contractName: contract ? contract?.title : '',
        },
    });

    const handleSave = (value: IProjectName) => {
        if (!contract) {
            return;
        }
        mutate(
            {
                contractName: value.contractName,
                contractId: contract.id,
            },
            {
                onSuccess: () => {
                    handleSnackBar('Successfully renamed!', 'success');
                    update({ contract: { ...contract, title: value.contractName } });
                },
                onError: (error) => {
                    DEFAULT_ERROR({ error, handleError: handleSnackBar });
                },
                onSettled: () => {
                    closeModal();
                },
            },
        );
    };

    const closeModal = () => {
        onHideModal();
        reset();
    };

    return (
        <Modal
            open={showModal}
            onClose={closeModal}
            title={'Sub Program Rename'}
            subtitle={<>Current name: {contract?.title}</>}
            actions={
                <>
                    <Button size={'large'} variant={'secondary'} onClick={closeModal}>
                        Cancel
                    </Button>

                    <Button
                        type={'submit'}
                        size={'large'}
                        variant={'primary'}
                        isLoading={isLoading}
                        disabled={isLoading}
                        onClick={handleSubmit(handleSave)}
                    >
                        Rename
                    </Button>
                </>
            }
        >
            <form onSubmit={handleSubmit(handleSave)} autoComplete={'off'}>
                <div className={classes.content}>
                    <div className={classes.inputWrapper}>
                        <label>New name</label>
                        <Controller
                            render={({ field }) => (
                                <TextInput
                                    {...field}
                                    id={'contractName'}
                                    className={classes.input}
                                    placeholder={'Enter sub-program name...'}
                                    maxLength={constants.MAX_SUB_PROGRAM_NAME_LENGTH}
                                    onClear={() => {
                                        setValue('contractName', '');
                                    }}
                                />
                            )}
                            name={'contractName'}
                            control={control}
                            rules={{
                                validate: {
                                    min: (value) => (value && value.length >= 2) || 'must be at least 2 characters',
                                    notValidName: (value) =>
                                        (value && value.toLowerCase() !== 'all') ||
                                        '"All" is not a valid sub-program name.',
                                },
                            }}
                        />
                        {errors.contractName && <CustomizedFormErrorMessage text={errors.contractName.message} />}
                    </div>
                </div>
            </form>
        </Modal>
    );
};
